import { useState } from 'react'
import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  PageHeader,
  Select,
  Stat,
  StatGrid,
  Table,
  Tabs,
  useAuthSession,
  usePermissions,
  type Column,
} from '@concours/ui'
import type { PermissionCode } from '@concours/shared'
import { BarList, Meter, StackedBar } from '../components/Charts'
import { errorMessage } from '../lib/errors'
import {
  fmtMoney,
  fmtNumber,
  fmtScore,
  useOverview,
  useTasks,
  type Overview,
  type StageReport,
  type Tasks,
} from '../lib/reports'

interface Readiness {
  status: 'ok' | 'degraded'
  checks: Record<string, { status: 'up' | 'down'; latencyMs: number }>
}

/** Travail en attente : permission, libellé, page de traitement. */
const TASKS: { key: keyof Tasks; perm: PermissionCode; label: string; href: string }[] = [
  {
    key: 'review',
    perm: 'candidature.review',
    label: 'Dossiers à instruire',
    href: '/candidatures',
  },
  {
    key: 'validate',
    perm: 'candidature.validate',
    label: 'Décisions de recevabilité à valider',
    href: '/candidatures',
  },
  {
    key: 'corrections',
    perm: 'corrections.approve',
    label: 'Corrections à valider',
    href: '/corrections',
  },
  { key: 'sheets', perm: 'notes.validate', label: 'Relevés à valider', href: '/commissions' },
  {
    key: 'approvals',
    perm: 'results.approve',
    label: 'Classements à approuver',
    href: '/resultats/approbations',
  },
  {
    key: 'notifications',
    perm: 'notifications.send',
    label: 'Notifications en échec',
    href: '/notifications/historique',
  },
]

/**
 * Tableau de bord par profil (cahier §13.1) : le travail en attente selon les
 * permissions, puis les indicateurs du périmètre (`reports.view`).
 */
export function DashboardPage() {
  const { t } = useTranslation()
  const { api } = useAuthSession()
  const { canAny, can } = usePermissions()
  const myTasks = TASKS.filter((x) => canAny([x.perm]))
  const canReports = can('reports.view')
  const tasks = useTasks(myTasks.length > 0)
  const [competitionId, setCompetitionId] = useState('')
  const overview = useOverview(competitionId, canReports)
  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<Readiness>('/health/ready', { raw: true }),
    refetchInterval: 30_000,
  })

  return (
    <>
      <PageHeader
        eyebrow={t('dashboard.eyebrow')}
        title={t('dashboard.title')}
        description={
          canReports
            ? 'Indicateurs de votre périmètre, calculés en temps réel. Aucune donnée nominative.'
            : t('dashboard.description')
        }
        actions={
          canReports &&
          overview.data && (
            <Select
              label="Concours"
              value={competitionId}
              options={[
                { value: '', label: 'Tous les concours de mon périmètre' },
                ...overview.data.competitions.map((c) => ({
                  value: c.id,
                  label: `${c.reference} — ${c.title}`,
                })),
              ]}
              onChange={(e) => setCompetitionId(e.target.value)}
            />
          )
        }
      />

      <div className="stack" style={{ gap: 'var(--space-6)' }}>
        {myTasks.length > 0 && (
          <Card>
            <CardHeader title="À traiter" />
            <CardBody>
              {tasks.isError ? (
                <Alert tone="danger">{errorMessage(tasks.error)}</Alert>
              ) : (
                <div className="task-grid">
                  {myTasks.map((x) => {
                    const value = tasks.data?.[x.key]
                    return (
                      <Link
                        key={x.key}
                        className="task-tile"
                        to={x.href}
                        data-zero={value === 0 ? 'true' : 'false'}
                      >
                        <strong>{value === undefined ? '…' : fmtNumber(value)}</strong>
                        <span className="muted">{x.label}</span>
                      </Link>
                    )
                  })}
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {canReports && overview.isError && (
          <Alert tone="danger">{errorMessage(overview.error)}</Alert>
        )}
        {canReports && overview.isPending && (
          <Card>
            <CardBody>Calcul des indicateurs…</CardBody>
          </Card>
        )}
        {overview.data && <Indicators o={overview.data} />}

        <Card>
          <CardHeader title={t('dashboard.apiState')} />
          <CardBody>
            {health.isPending && <p className="text-muted">{t('dashboard.checking')}</p>}
            {health.isError && (
              <Alert tone="danger" title={t('dashboard.unreachable')}>
                {health.error.message}
              </Alert>
            )}
            {health.data && (
              <div className="row" style={{ flexWrap: 'wrap' }}>
                {Object.entries(health.data.checks).map(([name, check]) => (
                  <Badge key={name} variant={check.status === 'up' ? 'success' : 'danger'} dot>
                    {name} : {check.status === 'up' ? t('dashboard.up') : t('dashboard.down')} ·{' '}
                    {check.latencyMs} ms
                  </Badge>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  )
}

type Distribution = 'gender' | 'region' | 'category'

function Indicators({ o }: { o: Overview }) {
  const [tab, setTab] = useState<Distribution>('region')
  const a = o.applications
  const columns: Column<Overview['applications']['byCompetition'][number]>[] = [
    {
      key: 'competition',
      header: 'Concours',
      cell: (c) => (
        <span className="stack" style={{ gap: 2 }}>
          <strong>{c.title}</strong>
          <span className="mono muted">{c.reference}</span>
        </span>
      ),
    },
    { key: 'started', header: 'Commencés', numeric: true, cell: (c) => fmtNumber(c.started) },
    { key: 'submitted', header: 'Déposés', numeric: true, cell: (c) => fmtNumber(c.submitted) },
    {
      key: 'admissible',
      header: 'Recevables',
      numeric: true,
      cell: (c) => fmtNumber(c.admissible),
    },
    {
      key: 'notAdmissible',
      header: 'Non recevables',
      numeric: true,
      cell: (c) => fmtNumber(c.notAdmissible),
    },
    {
      key: 'seats',
      header: 'Places',
      numeric: true,
      cell: (c) => (c.seats === null ? '—' : c.seats),
    },
  ]
  return (
    <>
      <StatGrid>
        {o.accounts && (
          <Stat
            label="Comptes candidats"
            value={fmtNumber(o.accounts.total)}
            hint={`+${fmtNumber(o.accounts.last30)} sur 30 jours`}
          />
        )}
        <Stat label="Candidatures commencées" value={fmtNumber(a.started)} />
        <Stat label="Dossiers déposés" value={fmtNumber(a.submitted)} />
        <Stat label="Recevables" value={fmtNumber(a.admissible)} />
        <Stat label="Non recevables" value={fmtNumber(a.notAdmissible)} />
      </StatGrid>

      <div className="dashboard-grid">
        <Card>
          <CardHeader title="Dossiers par statut" />
          <CardBody>
            <BarList items={a.byStatus} label="Dossiers par statut" />
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Répartition des dossiers déposés" />
          <CardBody className="stack">
            <Tabs
              value={tab}
              label="Répartition"
              onChange={(v) => setTab(v as Distribution)}
              items={[
                { value: 'region', label: 'Région' },
                { value: 'gender', label: 'Sexe' },
                { value: 'category', label: 'Catégorie' },
              ]}
            />
            <BarList
              items={o.distributions[tab]}
              label={`Dossiers déposés par ${tab === 'region' ? 'région' : tab === 'gender' ? 'sexe' : 'catégorie'}`}
            />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader title="Paiements" />
        <CardBody>
          <StatGrid>
            <Stat label="En attente" value={fmtNumber(o.payments.pending)} />
            <Stat label="Confirmés" value={fmtNumber(o.payments.confirmed)} />
            <Stat label="Échoués ou annulés" value={fmtNumber(o.payments.failed)} />
            <Stat
              label="Montant encaissé"
              value={fmtMoney(o.payments.amountConfirmed, o.payments.currency)}
              hint={o.payments.refunded ? `${o.payments.refunded} remboursé(s)` : undefined}
            />
          </StatGrid>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Par concours" />
        <Table
          columns={columns}
          rows={a.byCompetition}
          rowKey={(c) => c.id}
          caption="Candidatures par concours"
        />
      </Card>

      {o.stages.length > 0 && (
        <Card>
          <CardHeader title="Étapes de sélection" />
          <CardBody>
            {o.stages.map((s) => (
              <StageBlock key={s.id} s={s} />
            ))}
          </CardBody>
        </Card>
      )}
    </>
  )
}

function StageBlock({ s }: { s: StageReport }) {
  const retained = s.outcome.passed
  const columns: Column<StageReport['disciplines'][number]>[] = [
    { key: 'label', header: 'Discipline', cell: (d) => d.label },
    { key: 'count', header: 'Notes', numeric: true, cell: (d) => d.count },
    { key: 'absent', header: 'Absents', numeric: true, cell: (d) => d.absent },
    { key: 'mean', header: 'Moyenne', numeric: true, cell: (d) => fmtScore(d.mean) },
    { key: 'median', header: 'Médiane', numeric: true, cell: (d) => fmtScore(d.median) },
    {
      key: 'range',
      header: 'Min – max',
      numeric: true,
      cell: (d) => (d.count ? `${fmtScore(d.min)} – ${fmtScore(d.max)}` : '—'),
    },
    { key: 'stdev', header: 'Écart-type', numeric: true, cell: (d) => fmtScore(d.stdev) },
  ]
  return (
    <section className="stage-report stack" aria-label={`${s.label} — ${s.competition.reference}`}>
      <div
        className="row"
        style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-2)' }}
      >
        <strong>
          {s.position}. {s.label}{' '}
          <span className="mono muted" style={{ fontWeight: 400 }}>
            {s.competition.reference}
          </span>
        </strong>
        <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Badge variant="outline">{s.statusLabel}</Badge>
          {s.ranking && <Badge variant="info">Classement v{s.ranking.version}</Badge>}
        </span>
      </div>
      <div className="dashboard-grid">
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <span className="muted">
            Participation : {s.present} présent(s), {s.absent} absent(s) sur {s.population}
          </span>
          <Meter value={s.participation} label={`Participation — ${s.label}`} />
          <span className="muted">
            Places : {s.seats ?? '—'} · retenus : {retained}
          </span>
        </div>
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          {s.ranking ? (
            <>
              <StackedBar
                label={`Décisions — ${s.label}`}
                segments={[
                  { key: 'passed', label: 'Admis', value: s.outcome.passed },
                  {
                    key: 'supplementary',
                    label: 'Liste complémentaire',
                    value: s.outcome.supplementary,
                  },
                  { key: 'notRetained', label: 'Non retenus', value: s.outcome.notRetained },
                ]}
              />
              {(s.outcome.pending > 0 || s.ties > 0) && (
                <span className="muted">
                  {s.outcome.pending} décision(s) humaine(s) en attente · {s.ties} ex æquo
                </span>
              )}
            </>
          ) : (
            <span className="muted">Pas encore de classement calculé.</span>
          )}
        </div>
      </div>
      {s.disciplines.length > 0 && (
        <Table
          columns={columns}
          rows={s.disciplines}
          rowKey={(d) => d.label}
          caption={`Statistiques de notes — ${s.label}`}
        />
      )}
    </section>
  )
}
